import type { MaybeRefOrGetter } from 'vue';
import type { EventHandlerMap, Handler, WildcardHandler } from 'mitt';
import type { Simplify } from 'type-fest';
import mitt from 'mitt';
import { onBeforeUnmount, toValue, watch, readonly } from 'vue';

export type EventMessage<Key extends string = string, T = unknown> = {
  id?: string;
  event: Key;
  data: T;
};
export type ActualEventMap<EventMap extends Record<string, unknown>> = {
  [Key in keyof EventMap]: EventMap[Key] extends EventMessage ? { id?: string } & EventMap[Key] : EventMessage<string & Key, EventMap[Key]>;
};
type EmitterMap<EventMap extends Record<string, EventMessage>> = { [Key in keyof EventMap]: Handler<Simplify<EventMap[Key]>> } & {
  '*': Handler<Simplify<EventMap[keyof EventMap]>>;
};

export type Emitter<EventMap extends Record<string, EventMessage>> = {
  all: EventHandlerMap<EventMap>;
  on: <Key extends '*' | keyof EventMap>(type: Key, handler: Handler<Simplify<EventMap[Key extends '*' ? keyof EventMap : Key]>>) => () => void;
  onMap: <Events extends Partial<EmitterMap<EventMap>>>(map: Events) => () => void;
  emit: <Key extends keyof EventMap>(type: Key, event: EventMap[Key] | EventMap[Key]['data']) => void;
  useSubscriber: <Events extends Partial<EmitterMap<EventMap>>>(mapVariant: MaybeRefOrGetter<Events>, options?: { enabled?: MaybeRefOrGetter<boolean> }) => void;
  waitForEvent: <K extends keyof EventMap, S extends EventMap[K]>(type: K, options?: {
    predicate?: ((event: EventMap[K]) => boolean) | ((event: EventMap[K]) => event is S);
    timeout?: number;
  }) => Promise<S>;
};

export function createEmitter<EventMap extends Record<string, unknown>>({ ttl = 0 }: { ttl?: number } = {}) {
  type ActualEvents = ActualEventMap<EventMap>;
  const emitterInstance = mitt<ActualEvents>();
  const emitter: Emitter<ActualEvents> = {
    all: emitterInstance.all,
    on(type, handler) {
      const fn: Handler<ActualEvents[typeof type]> | WildcardHandler<ActualEvents> = (...args: [eventType: typeof type, event: ActualEvents[typeof type]] | [event: ActualEvents[keyof ActualEvents]]) => {
        const event = args.length === 1 ? args[0] : args[1];
        handler(event as any);
      };
      emitterInstance.on(type as unknown as any, fn);
      return () => {
        emitterInstance.off(type as unknown as any, fn);
      };
    },
    onMap<Events extends Partial<EmitterMap<ActualEvents>>>(map: Events) {
      const unsubscribers = Object.keys(map).map((type) => {
        const actualType = type as '*' | keyof ActualEvents;
        if (!map[actualType]) {
          return () => {};
        }
        return emitter.on(actualType, map[actualType] as any);
      });

      return () => {
        unsubscribers.forEach((unsubscriber) => {
          unsubscriber();
        });
      };
    },
    emit(type, event) {
      let normalizedEvent: ActualEvents[typeof type];
      if (typeof event === 'object' && event !== null && 'event' in event && event.event === type) {
        normalizedEvent = event;
      } else {
        normalizedEvent = {
          event: type,
          data: event
        } as ActualEvents[typeof type];
      }

      return emitterInstance.emit(type, readonly(normalizedEvent) as ActualEvents[typeof type]);
    },
    useSubscriber(mapVariant, { enabled = true } = {}) {
      let unsubscribe = () => {};
      watch(() => ({
        map: toValue(mapVariant),
        enabled: toValue(enabled)
      }), ({ map, enabled }) => {
        unsubscribe();
        if (!enabled) {
          return;
        }
        unsubscribe = emitter.onMap(map);
      }, {
        immediate: true
      });

      onBeforeUnmount(() => {
        unsubscribe();
      });
    },
    waitForEvent(type, { timeout, predicate } = {}) {
      return new Promise((resolve, reject) => {
        const dataList = dataCache.get(type) ?? [];
        const listLength = dataList.length;
        for (let index = listLength - 1; index >= 0; index--) {
          const message = dataList[index] as ActualEvents[typeof type];
          if (!predicate || predicate(message)) {
            resolve(message as any);
            return;
          }
        }

        const resolvers = awaiters.get(type) || [];
        resolvers.push({
          resolve: resolve as (value: ActualEvents[keyof ActualEvents]) => void,
          predicate: predicate as (event: ActualEvents[keyof ActualEvents]) => boolean
        });
        awaiters.set(type, resolvers);

        if (timeout) {
          setTimeout(() => {
            reject(new Error('waitForEvent finished by timeout'));
          }, timeout);
        }
      });
    }
  };

  const awaiters = new Map<keyof ActualEvents, Array<{
    resolve: (value: ActualEvents[keyof ActualEvents]) => void;
    predicate?: (event: ActualEvents[keyof ActualEvents]) => boolean;
  }>>();
  const dataCache = new Map<keyof ActualEvents, ActualEvents[keyof ActualEvents][]>();
  emitter.on('*', (event) => {
    const message = event as ActualEvents[keyof ActualEvents];
    const key = message.event;
    const dataList = dataCache.get(key) ?? [];
    dataList.push(message);
    dataCache.set(key, dataList);
    setTimeout(() => {
      const index = dataList.findIndex((data) => data === message);
      dataList.splice(index, 1);
    }, ttl ?? 0);

    const resolvers = awaiters.get(key);
    if (!resolvers) {
      return;
    }

    const resolversRest = resolvers.filter(({ resolve, predicate }) => {
      if (!predicate || predicate(message)) {
        resolve(message);
        return false;
      }
      return true;
    });
    awaiters.set(key, resolversRest);
  });

  return emitter;
}
